/**
 * Add to Cart confirmation
 * ------------------------
 * Dependency-free enhancement layer for the Grid theme.
 *
 * grid.js performs the actual /cart/add.js request and reveals (and later
 * clears) the `.product-message.success-message`. This layer only adds visual
 * feedback on top of that existing result:
 *
 *   1. an "Adding…" state on the button the moment it is clicked,
 *   2. an "Added!" confirmation on the button when the add succeeds
 *      (text only — the button keeps its normal colours, it never turns green),
 *   3. a bump on the header cart icon(s),
 *   4. pops the header mini-cart drawer open so the new item is visible.
 *
 * The drawer is the theme's own dropdown shown on demand, so it looks the same
 * everywhere. On mobile (no :hover) a tap on the cart icon opens it, a tap
 * outside closes it, and it auto-closes after a few seconds — and the CSS just
 * narrows it to fit the phone.
 *
 * The small circle-check badge on the success message is pure CSS and lives on
 * the message element itself, so it appears and disappears together with the
 * message text as a single unit — this script does not touch it.
 *
 * It uses event delegation + a scoped MutationObserver, so it works on product
 * pages, in Quick Shop, and on forms injected after load, and degrades
 * gracefully if the theme changes.
 */
(function () {
  'use strict';

  var theme = window.Theme || {};
  var ADDING_TEXT = theme.addToCartAddingText || 'Adding…';
  var ADDED_TEXT = theme.addToCartAddedText || 'Added!';

  var ADDED_DURATION = 1600; // how long the "Added!" state remains on the button
  var SAFETY_TIMEOUT = 6000; // revert the loading state if no result is seen
  var CART_OPEN_DURATION = 4000; // how long the mini-cart stays open after an add

  // Transient per-button state so repeated clicks never stack observers/timers.
  var states = new WeakMap();

  function isAddToCartButton(el) {
    return !!el &&
      !!el.classList &&
      el.classList.contains('add-to-cart') &&
      !el.classList.contains('disabled') &&
      !el.disabled;
  }

  // The success/error message lives in the same product form as the button.
  function findMessageEl(button) {
    var scope = button.closest('[data-product-form]') ||
      button.closest('.product__form') ||
      button.form ||
      button.closest('form');
    return scope ? scope.querySelector('.product-message') : null;
  }

  function clearTimers(state) {
    if (state.safetyTimer) clearTimeout(state.safetyTimer);
    if (state.addedTimer) clearTimeout(state.addedTimer);
  }

  function cleanup(button) {
    var state = states.get(button);
    if (!state) return;
    if (state.observer) state.observer.disconnect();
    clearTimers(state);
    states.delete(button);
  }

  function resetButton(button) {
    var state = states.get(button);
    button.classList.remove('is-adding', 'is-added');
    if (state && typeof state.originalValue === 'string') {
      button.value = state.originalValue;
    }
  }

  // Replay the bump on every add, including consecutive ones.
  function bumpCart() {
    var carts = document.querySelectorAll('[data-cart-count]');
    Array.prototype.forEach.call(carts, function (cart) {
      cart.classList.remove('atc-cart-bump');
      void cart.offsetWidth; // force reflow so the animation restarts
      cart.classList.add('atc-cart-bump');
    });
  }

  // The mini-cart drawer: opens on add, on every breakpoint. The CSS keeps it
  // hidden on the cart page and when the cart is empty.
  var cartOpenTimer = null;

  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 1079px)').matches;
  }

  // Dismiss the drawer when the user clicks/taps anywhere outside it — the same
  // way the theme's hover dropdown goes away when you move off it.
  function onOutsideClick(event) {
    var t = event.target;
    if (t && t.closest && t.closest('.mini-cart-wrapper')) return; // inside — keep open
    closeMiniCart();
  }

  function closeMiniCart() {
    document.removeEventListener('click', onOutsideClick);
    if (cartOpenTimer) {
      clearTimeout(cartOpenTimer);
      cartOpenTimer = null;
    }
    var open = document.querySelectorAll('.mini-cart-wrapper.atc-cart-open');
    Array.prototype.forEach.call(open, function (w) {
      w.classList.remove('atc-cart-open');
    });
  }

  function openMiniCart() {
    var wrappers = document.querySelectorAll('.mini-cart-wrapper');
    if (!wrappers.length) return;
    Array.prototype.forEach.call(wrappers, function (w) {
      w.classList.add('atc-cart-open');
    });

    // Auto-close after a few seconds on every breakpoint (on desktop :hover
    // holds it open past that, same as the theme's own dropdown).
    if (cartOpenTimer) { clearTimeout(cartOpenTimer); cartOpenTimer = null; }
    cartOpenTimer = setTimeout(closeMiniCart, CART_OPEN_DURATION);

    // Activate "tap outside to dismiss" only after the current (triggering)
    // click has finished, so this same click doesn't immediately close it.
    document.removeEventListener('click', onOutsideClick);
    setTimeout(function () {
      document.addEventListener('click', onOutsideClick);
    }, 0);
  }

  // On mobile the cart icon has no hover dropdown and normally just navigates
  // to /cart. Make a tap open (or close) the drawer instead, so the full
  // mini-cart is reachable without leaving the page. This covers BOTH cart
  // links the theme can show: the top-bar `.cart-count` and the main-header
  // `.sticky-header-cart-count` — either one opens the same drawer. An empty
  // cart still navigates to the cart page.
  function onCartLinkClick(event) {
    if (!isMobile()) return;
    var t = event.target;
    var link = t && t.closest ? t.closest('.cart-count, .sticky-header-cart-count') : null;
    if (!link) return;
    var wrapper = document.querySelector('.mini-cart-wrapper');
    var mc = wrapper ? wrapper.querySelector('.mini-cart') : null;
    if (!mc || mc.classList.contains('empty')) return; // empty — let it go to /cart
    event.preventDefault();
    if (wrapper.classList.contains('atc-cart-open')) {
      closeMiniCart();
    } else {
      openMiniCart();
    }
  }

  function showAdded(button) {
    var state = states.get(button);
    if (!state) return;

    button.classList.remove('is-adding');
    button.classList.add('is-added');
    button.value = ADDED_TEXT; // plain text, normal colours (no ✓, no green)
    bumpCart();
    openMiniCart();

    state.addedTimer = setTimeout(function () {
      resetButton(button);
      cleanup(button);
    }, ADDED_DURATION);
  }

  function handleResult(button, msgEl) {
    var state = states.get(button);
    if (!state) return;

    var hasContent = msgEl.textContent.trim() !== '';

    // Success: the theme injects the message text and adds `success-message`.
    if (hasContent && msgEl.classList.contains('success-message')) {
      if (state.observer) state.observer.disconnect();
      clearTimers(state);
      showAdded(button);
      return;
    }

    // Error: quietly revert and let the theme display its own error message.
    if (hasContent && msgEl.classList.contains('error-message')) {
      resetButton(button);
      cleanup(button);
    }
  }

  function onClick(event) {
    var target = event.target;
    var button = target && target.closest ? target.closest('.add-to-cart') : null;
    if (!isAddToCartButton(button)) return;

    // Already mid-confirmation for this button — let the theme handle it.
    if (states.has(button)) return;

    var msgEl = findMessageEl(button);

    var state = {
      originalValue: button.value,
      observer: null,
      safetyTimer: null,
      addedTimer: null
    };
    states.set(button, state);

    // Instant feedback (the theme adds the item ~500ms later).
    button.classList.add('is-adding');
    button.value = ADDING_TEXT;

    if (msgEl) {
      state.observer = new MutationObserver(function () {
        handleResult(button, msgEl);
      });
      state.observer.observe(msgEl, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class']
      });
    }

    // Safety net: if no result is detected (network error, or cart-redirection
    // where the page navigates away), clean the loading state back up.
    state.safetyTimer = setTimeout(function () {
      resetButton(button);
      cleanup(button);
    }, SAFETY_TIMEOUT);
  }

  // Capture phase so our observer is attached before grid.js mutates the DOM.
  document.addEventListener('click', onClick, true);

  // Mobile: tapping the cart icon toggles the drawer instead of navigating.
  document.addEventListener('click', onCartLinkClick);
})();
